import { useState } from "react";
import { Link } from "react-router-dom";
import { AMIR_COIN, AMIR_TOKEN } from "@/lib/coin";
import {
  ShieldCheck, Target, ScrollText, Wallet, Copy, Check, ExternalLink,
  Eye, Lock, Users, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

function Pillar({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#6A0DAD] rounded-2xl p-7 shadow-sm">
      <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#6A0DAD] text-white mb-4"><Icon size={22} /></span>
      <h2 className="font-heading text-xl font-semibold text-[#1A202C] mb-3">{title}</h2>
      {children}
    </div>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className="text-[#4A5568] leading-relaxed flex gap-2">
          <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function Transparency() {
  const [copied, setCopied] = useState(false);
  const copyMint = async () => {
    try {
      await navigator.clipboard.writeText(AMIR_COIN.mint);
      setCopied(true);
      toast.success("Contract address copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-12">
      <Link to="/wallet" data-testid="transparency-back" className="inline-flex items-center gap-2 text-[#4A5568] hover:text-[#1A202C] font-heading mb-6">
        <ArrowLeft size={18} /> Back
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="text-[#6A0DAD]" size={30} />
        <h1 className="font-display text-4xl md:text-5xl text-[#1A202C]">Token Transparency</h1>
      </div>
      <p className="text-[#4A5568] mb-8 max-w-2xl leading-relaxed">
        AMIR is built on trust. Here are its clear use cases, public rules, and the links to independently
        verify everything on-chain — no hidden control, nothing to take on faith.
      </p>

      {/* Verify strip */}
      <div className="bg-[#6A0DAD]/8 border border-[#6A0DAD]/30 rounded-2xl p-6 mb-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-heading uppercase tracking-widest text-[#B8972E] mb-1">Verified on Solana</p>
            <p className="font-heading font-semibold text-[#1A202C]">
              Total supply: {AMIR_TOKEN.totalSupply} AMIR
            </p>
          </div>
          <button data-testid="transparency-copy-mint" onClick={copyMint}
            className="flex items-center gap-2 bg-white border border-[#6A0DAD]/40 rounded-xl px-4 py-2.5 font-mono text-sm text-[#1A202C] hover:border-[#6A0DAD] transition-colors max-w-full">
            <span className="truncate">{AMIR_COIN.mint}</span>
            {copied ? <Check size={16} className="text-green-600 shrink-0" /> : <Copy size={16} className="text-[#6A0DAD] shrink-0" />}
          </button>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <a href={AMIR_COIN.solscanUrl} target="_blank" rel="noopener noreferrer" data-testid="transparency-solscan"
            className="inline-flex items-center gap-2 bg-[#6A0DAD] text-white hover:bg-[#58098F] rounded-full px-5 py-2.5 font-heading font-semibold text-sm transition-colors">
            Verify on Solscan <ExternalLink size={15} />
          </a>
          <a href={`https://solscan.io/token/${AMIR_COIN.mint}#holders`} target="_blank" rel="noopener noreferrer" data-testid="transparency-holders"
            className="inline-flex items-center gap-2 bg-white border border-[#6A0DAD] text-[#6A0DAD] hover:shadow-md rounded-full px-5 py-2.5 font-heading font-semibold text-sm transition-all">
            <Users size={15} /> See all holders
          </a>
          <a href={AMIR_COIN.pumpUrl} target="_blank" rel="noopener noreferrer" data-testid="transparency-pump"
            className="inline-flex items-center gap-2 bg-white border border-[#6A0DAD] text-[#6A0DAD] hover:shadow-md rounded-full px-5 py-2.5 font-heading font-semibold text-sm transition-all">
            View on pump.fun <ExternalLink size={15} />
          </a>
        </div>
      </div>

      {/* Four pillars */}
      <div className="grid md:grid-cols-2 gap-4">
        <Pillar icon={Target} title="Clear use cases" testid="pillar-use-cases">
          <List items={AMIR_TOKEN.useCases} />
        </Pillar>
        <Pillar icon={ScrollText} title="Public rules" testid="pillar-rules">
          <List items={AMIR_TOKEN.rules} />
        </Pillar>
        <Pillar icon={Wallet} title="Verifiable fund flows" testid="pillar-fund-flows">
          <p className="text-[#4A5568] leading-relaxed mb-3">{AMIR_TOKEN.launch}</p>
          {AMIR_TOKEN.treasuryWallet ? (
            <a href={`https://solscan.io/account/${AMIR_TOKEN.treasuryWallet}`} target="_blank" rel="noopener noreferrer"
              data-testid="transparency-treasury"
              className="inline-flex items-center gap-2 text-[#6A0DAD] font-heading font-semibold text-sm">
              Track the community wallet on Solscan <ExternalLink size={15} />
            </a>
          ) : (
            <p className="text-[#4A5568] leading-relaxed text-sm">
              Every transaction lives on the Solana blockchain — anyone can trace buys, sells, and transfers
              through the Solscan links above. Nothing happens off the record.
            </p>
          )}
        </Pillar>
        <Pillar icon={Lock} title="No hidden control" testid="pillar-no-hidden-control">
          <List items={AMIR_TOKEN.commitments} />
        </Pillar>
      </div>

      <div className="mt-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-2xl p-6 flex gap-3">
        <Eye size={20} className="text-[#B8972E] shrink-0 mt-0.5" />
        <p className="text-sm text-[#4A5568] leading-relaxed">
          AMIR is a community and purpose token, not an investment or financial product. Crypto is volatile
          and carries risk — please only participate with what you can afford, and always do your own research.
        </p>
      </div>
    </div>
  );
}
